import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    getMonthsByFinancialYear,
    getMonthsByYear,
    monthKeyToDisplay,
    monthsEnum,
    periodHeaderToMonthKey,
} from '../../../../../utils/dateUtils';

import notAvailable from '../../../../../assets/not-available.svg';
import greenTick from '../../../../../assets/available.svg';
import yellowTick from '../../../../../assets/yellow-tick.svg';
import hyphenGrey from '../../../../../assets/hyphen-grey.svg';
import styles from './scss/MonthlyView.module.scss';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { fetchGstr3bData } from '../../../../../store/dashboard/revenueVendorExpenses/revenueVendorExpensesApiHelpers';
import { GRAPH_NAMES } from '../../../../../store/dashboard/constants';
import { EPF_WINDOW_MONTHS } from '../../../../../store/dashboard/compliancesBefe/constants';
import type { FinancialYearMonthCell } from '../../../../../utils/types/financialYearGrid';
import { useEpfFilingDetails } from './EpfFilingDetails/useEpfFilingDetails';
import EpfHoverContent from './EpfFilingDetails/EpfHoverContent';
import { EPF_HOVERABLE_STATUSES } from './EpfFilingDetails/constants';
import { FILING_GRAPH_NAMES, FY_GRID_GRAPH_NAMES, FY_GRID_LEGEND, FY_GRID_STATUS_ICONS } from './constants';
import { complianceStatusUnavailable as statusUnavailable } from '../../../../../assets/hostedassets';

interface SmallMonthlyViewProps {
    height: any;
    error: any;
    width: any;
    graphData: any;
    graphName: string;
    isPDFReport?: boolean;
}

type FilingComplianceEntry = {
    gstin: string;
    filingStatus: 'Filed' | 'Delayed' | 'Default' | string;
    noOfDaysOfDelay: number;
    dateOfFiling?: string;
};

type DataSourceEntry = { gstNumber?: string; state?: string | null };

const dataSourceListCache = new Map<string, Promise<DataSourceEntry[]>>();

const GstHoverContent = ({
    entries,
    monthLabel,
    dataSourceList,
}: {
    entries: FilingComplianceEntry[];
    monthLabel: string;
    dataSourceList: DataSourceEntry[];
}) => (
    <div className={styles.Tooltip}>
        <div className={styles.TopText}>
            GST Returns Status <span className={styles.Divider}>|</span> <span className={styles.Month}>{monthLabel}</span>
        </div>
        <div className={styles.Gsts}>
            {entries.map((entry, i) => {
                const stateName = dataSourceList.find((d) => d.gstNumber === entry.gstin)?.state;
                const isDefault = entry.filingStatus === 'Default';
                return (
                    <div className={styles.GstRow} key={`${entry.gstin}-${i}`}>
                        <div className={styles.Left}>{stateName ? `${stateName} (${entry.gstin})` : entry.gstin}</div>
                        <div className={styles.Right}>
                            <div
                                className={clsx(styles.Badge, {
                                    [styles.Defaulted]: isDefault,
                                    [styles.Delayed]: !isDefault,
                                })}
                            >
                                {isDefault ? 'Default' : `${entry.noOfDaysOfDelay} ${entry.noOfDaysOfDelay === 1 ? 'Day' : 'Days'} Delay`}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    </div>
);

async function fetchDataSourceList(orgId: string): Promise<DataSourceEntry[]> {
    try {
        const data: any = await fetchGstr3bData({ orgId }, {});
        return data?.AION_GET_GST_LIST?.totalGstList ?? [];
    } catch {
        return [];
    }
}

function loadDataSourceList(orgId: string): Promise<DataSourceEntry[]> {
    if (dataSourceListCache.has(orgId)) {
        return dataSourceListCache.get(orgId)!;
    }

    const promise = fetchDataSourceList(orgId);
    dataSourceListCache.set(orgId, promise);
    return promise;
}

function MonthlyView({ height, graphData, graphName, isPDFReport = false }: SmallMonthlyViewProps) {
    const { months, datesObj, startDate } = getMonthsByYear(24);
    const plots = graphData?.plots?.[0]?.plotValues || [];
    let data = {};
    let delayDaysData = {};
    let entriesByXValue: Record<string, FilingComplianceEntry[]> = {};
    plots.forEach((plot) => {
        data[plot.xValue] = plot.yValue;
        delayDaysData[plot.xValue] = plot.yValue2;
        const filings = plot.extras?.filings as FilingComplianceEntry[] | undefined;
        if (filings && filings.length) entriesByXValue[plot.xValue] = filings;
    });

    const params = useParams();
    const orgId = params.id;
    const isFilingChart = FILING_GRAPH_NAMES.has(graphName);
    const [dataSourceList, setDataSourceList] = useState<DataSourceEntry[]>([]);
    const isFyGrid = FY_GRID_GRAPH_NAMES.has(graphName);
    const { paymentsByXValue } = useEpfFilingDetails(graphData, graphName === GRAPH_NAMES.EPF_COMPLIANCE && !isPDFReport);

    useEffect(() => {
        if (!isFilingChart || !orgId || isPDFReport) return;
        let active = true;
        loadDataSourceList(orgId).then((list) => {
            if (active) setDataSourceList(list);
        });
        return () => {
            active = false;
        };
    }, [isFilingChart, orgId, isPDFReport]);

    const getAvailabilityStatusIcon = (month: string, year: string, data: any, ind, i) => {
        const date = `${monthsEnum[month].name}_${year}`;
        // if (start.isAfter(current)) return hyphenGrey;
        if (data?.[date]) {
            if (data?.[date] === 1) return greenTick;
            if (data?.[date] === 2) return yellowTick;
            if (data?.[date] === 3) return notAvailable;
        }
        return hyphenGrey;
    };

    const Legend = () => {
        const legendData = isFyGrid
            ? FY_GRID_LEGEND
            : [
                  { img: hyphenGrey, label: 'Data Unavailable' },
                  { img: greenTick, label: 'On-Time' },
                  { img: yellowTick, label: 'Delayed' },
                  { img: notAvailable, label: 'Default' },
              ];
        return (
            <div
                className={clsx(styles.LegendContainer, {
                    [styles.PDFReportLegend]: isPDFReport,
                    [styles.FyGridLegend]: isFyGrid,
                })}
            >
                {legendData.map((legend, i) => (
                    <div className={styles.Legend} key={i}>
                        <img src={legend.img} alt="yellow" height={isFyGrid ? '16px' : '10px'} />
                        {legend.label}
                    </div>
                ))}
            </div>
        );
    };

    /**
     * Render the cell content. Delayed cells show a day-count badge; defaulted /
     * unavailable / on-time cells show their icon.
     */
    const renderCell = (month: string, year: string, ind: number, i: number) => {
        const date = `${monthsEnum[month].name}_${year}`;
        const status = data?.[date];
        return status === 2 ? (
            <span className={styles.DelayCount}>{delayDaysData?.[date]}</span>
        ) : (
            <img src={getAvailabilityStatusIcon(month, year, data, ind, i)} alt="avl" height={isPDFReport ? '6px' : '9px'} />
        );
    };

    /**
     * FY-grid cell. GST cells hover their delayed/defaulted GSTINs. EPF cells hover payments only
     * for a filed or defaulted month: an Unavailable month can still carry a Karza default payment
     * the grid filtered out, and must stay silent.
     */
    const renderFyGridCell = ({ xValue, isFuture }: FinancialYearMonthCell) => {
        if (isFuture) return null;
        const status = data?.[xValue];
        const cellInner =
            status === 2 ? (
                <span className={clsx(styles.DelayCount, styles.FyGridDelayCount)}>{delayDaysData?.[xValue]}</span>
            ) : (
                <img src={FY_GRID_STATUS_ICONS[status] ?? statusUnavailable} alt="avl" height={isPDFReport ? '10px' : '16px'} />
            );

        const hoverContent = isPDFReport ? null : isFilingChart ? getGstHover(xValue) : getEpfHover(xValue, status);
        if (!hoverContent) return cellInner;
        return (
            <Tooltip
                content={hoverContent}
                direction={'top'}
                usePortal={true}
                hideDelay={300}
                fitContent={!isFilingChart}
                contentClassName={isFilingChart ? undefined : styles.EpfTooltipContent}
            >
                {cellInner}
            </Tooltip>
        );
    };

    const getGstHover = (xValue: string) => {
        const entries = entriesByXValue[xValue];
        if (!entries?.length) return null;
        const monthLabel = monthKeyToDisplay(periodHeaderToMonthKey(xValue));
        return <GstHoverContent entries={entries} monthLabel={monthLabel} dataSourceList={dataSourceList} />;
    };

    const getEpfHover = (xValue: string, status: number) => {
        const payments = paymentsByXValue[xValue];
        if (!EPF_HOVERABLE_STATUSES.has(status) || !payments?.length) return null;
        return <EpfHoverContent xValue={xValue} payments={payments} />;
    };

    const renderFyGridTable = () => {
        const { rows } = getMonthsByFinancialYear(EPF_WINDOW_MONTHS);
        return (
            <table className={clsx(styles.Table, styles.FyGridTable)}>
                <thead>
                    <tr>
                        <th className={styles.FirstColumn}> </th>
                        {rows[0]?.cells.map(({ xValue }) => (
                            <th className={styles.MonthName} key={xValue}>
                                {xValue.split('_')[0]}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.label}>
                            <td className={clsx(styles.Year, styles.FyLabel)}>{row.label}</td>
                            {row.cells.map((cell) => (
                                <td key={cell.xValue} className={styles.Item}>
                                    <div className={styles.Img}>{renderFyGridCell(cell)}</div>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div
            className={clsx(styles.SmallMonthlyView, {
                [styles.PDFReport]: isPDFReport,
                [styles.FyGrid]: isFyGrid,
            })}
            style={{
                height: isPDFReport || isFyGrid ? 'auto' : height,
            }}
        >
            <Legend />
            {!isPDFReport && !isFyGrid && (
                <div className={styles.Top} style={{ margin: '10px 0' }}>
                    <div className={styles.TopLeft}>
                        <div className={styles.Row}></div>
                    </div>
                </div>
            )}

            <div className={clsx(styles.TableContainer)}>
                {isFyGrid ? (
                    renderFyGridTable()
                ) : (
                    <table className={clsx(styles.Table)}>
                        <thead>
                            <tr>
                                <th className={styles.FirstColumn}> </th>
                                {months.map((month: any, i: number) => (
                                    <th className={styles.MonthName} key={`${month}${i}`}>
                                        {monthsEnum[month]?.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {Object.keys(datesObj).map((year, i) => (
                                <tr key={i}>
                                    <td className={styles.Year}>{year}</td>
                                    {datesObj[year].map((month: any, ind: any) => (
                                        <td key={ind} className={styles.Item}>
                                            <div className={styles.Img}>{renderCell(month, year, ind, i)}</div>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default MonthlyView;
